import { attachmentsValue, type TemplateFormAttachment } from "./templateForm";
import { buildTemplateFormPdfModel, type TemplateFormPdfInput } from "./templateFormPdf";
import { renderTemplateFormPdf } from "./templateFormPdfRenderer";
import { appendAttachmentsToPdf } from "./templateFormPdfAttachments";
import { resolveAttachmentBytes } from "./resolveAttachmentBytes";

export interface RenderedTemplateForm {
  bytes: Uint8Array;
  /** Attachments the PDF could only name — reported so the UI can say so. */
  notAppended: string[];
}

/** Every attachment the form holds, across whichever fields declare them. */
export function formAttachments(input: TemplateFormPdfInput): TemplateFormAttachment[] {
  return input.form.sections.flatMap((section) =>
    section.fields
      .filter((field) => field.type === "attachments")
      .flatMap((field) => attachmentsValue(input.values, field.id))
  );
}

/**
 * The document as it is filed: the form itself, followed by the attachments
 * when "Add attachments to the PDF" is ticked.
 *
 * One function for both the preview and the save, so what the user checks with
 * **Open PDF** is byte-for-byte what gets stored.
 */
export async function renderTemplateFormDocument(
  input: TemplateFormPdfInput
): Promise<RenderedTemplateForm> {
  const bytes = await renderTemplateFormPdf(buildTemplateFormPdfModel(input));
  if (!input.attachmentsInPdf) return { bytes, notAppended: [] };

  const { resolved, unresolved } = await resolveAttachmentBytes(formAttachments(input));
  const merged = await appendAttachmentsToPdf(bytes, resolved);
  // Both ways an attachment can fail to make it into the PDF, in one list: its
  // bytes could not be fetched, or they could and pdf-lib could not draw them.
  // The tick box promised the same thing either way.
  return { bytes: merged.bytes, notAppended: [...unresolved, ...merged.notAppended] };
}
